import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  CircularProgress,
  Alert,
  AlertTitle,
  Paper,
  Divider,
  Stack
} from '@mui/material';
import { 
  Payment as PaymentIcon,
  AssignmentLate as LateIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';

import RealizarPagamento from '../Pagamentos/RealizarPagamento';
import { usePlanosPagamento } from '../../contexts/PlanosPagamentoContext';
import { useAuth } from '../../hooks/useAuth';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import { formatarMoeda } from '../../utils/formatters';
import { listarPlanosPagamentoEstudante } from '../../services/planosPagamentoService';
import * as pagamentoService from '../../services/pagamentoService';
import api from '../../services/api';

const PlanosPagamentoEstudante = () => {
  const [planosPagamento, setPlanosPagamento] = useState([]);
  const [prestacaoSelecionada, setPrestacaoSelecionada] = useState(null);
  const [dialogPagamento, setDialogPagamento] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detalhesMultaPorPrestacao, setDetalhesMultaPorPrestacao] = useState({});
  const { user } = useAuth();
  const { recarregarPlanos } = usePlanosPagamento();
  const { adicionarNotificacao } = useNotificacao();

  const fetchPlanos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listarPlanosPagamentoEstudante();
      if (response?.data) {
        setPlanosPagamento(response.data);
        setError(null);
      } else {
        throw new Error('Dados inválidos recebidos do servidor');
      }
    } catch (err) {
      console.error('Erro ao carregar planos:', err);
      setError(err.message || 'Erro ao carregar planos de pagamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.tipo === 'estudante') {
      fetchPlanos();
    }
  }, [user, fetchPlanos]);

  // Novo useEffect para calcular multas
  useEffect(() => {
    const calcularMultasParaPrestacoes = async () => {
      const prestacoes = planosPagamento.flatMap(plano => 
        plano.prestacoes.filter(p => p.vencida)
      );
      
      for (const prestacao of prestacoes) {
        try {
          const calculo = await pagamentoService.calcularValorComMulta(prestacao.id);
          setDetalhesMultaPorPrestacao(prev => ({
            ...prev,
            [prestacao.id]: calculo.data
          }));
        } catch (error) {
          console.error(`Erro ao calcular multa para prestação ${prestacao.id}:`, error);
        }
      }
    };

    if (planosPagamento.length > 0) {
      calcularMultasParaPrestacoes();
    }
  }, [planosPagamento]);

  const getStatusInfo = (prestacao) => {
    if (prestacao.vencida) {
      return { color: 'error', icon: LateIcon, label: 'Vencida' };
    }
    if (prestacao.status === 'PAGO') {
      return { color: 'success', icon: CheckCircleIcon, label: 'Pago' };
    }
    if (prestacao.diasAteVencimento <= 5) {
      return { color: 'warning', icon: WarningIcon, label: 'Próximo ao Vencimento' };
    }
    return { color: 'info', icon: PaymentIcon, label: 'Pendente' };
  };

  const handleCloseDialog = useCallback(() => {
    setDialogPagamento(false);
    setPrestacaoSelecionada(null);
  }, []);

  const handlePagar = useCallback(async (prestacao) => {
    try {
      // Se tiver detalhes de multa, usa eles
      const detalhesMulta = detalhesMultaPorPrestacao[prestacao.id];
      setPrestacaoSelecionada({
        ...prestacao,
        valorTotal: detalhesMulta?.valorTotal || prestacao.valor,
        valorMulta: detalhesMulta?.valorMulta || 0,
        valorOriginal: detalhesMulta?.valorPrestacao || prestacao.valor
      });
      setDialogPagamento(true);
    } catch (error) {
      console.error('Erro ao preparar pagamento:', error);
      adicionarNotificacao(
        error.message || 'Erro ao calcular valor do pagamento',
        'error'
      );
    }
  }, [adicionarNotificacao, detalhesMultaPorPrestacao]);

  const handleSuccess = useCallback(async () => {
    handleCloseDialog();
    try {
      setLoading(true);
      const response = await listarPlanosPagamentoEstudante();
      if (response?.data) {
        setPlanosPagamento(response.data);
        setError(null);
        await recarregarPlanos?.();
      }
    } catch (err) {
      console.error('Erro ao recarregar planos:', err);
      setError(err.message || 'Erro ao atualizar planos de pagamento');
    } finally {
      setLoading(false);
    }
  }, [handleCloseDialog, recarregarPlanos]);

  const handleDownloadComprovativo = useCallback(async (pagamentoId) => {
    try {
      const response = await api.get(`/pagamentos/${pagamentoId}/comprovativo`, {
        responseType: 'blob'
      });
      
      if (response.headers['content-type'] === 'application/pdf') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `comprovativo-pagamento-${pagamentoId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        adicionarNotificacao('Comprovativo baixado com sucesso', 'success');
      } else {
        throw new Error('Formato de arquivo inválido');
      }
    } catch (error) {
      console.error('Erro ao baixar comprovativo:', error);
      adicionarNotificacao(
        error.response?.data?.message || 'Erro ao baixar comprovativo', 
        'error'
      );
    }
  }, [adicionarNotificacao]);

  const renderResumoFinanceiro = (plano) => (
    <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary">Próximo Vencimento</Typography>
          <Typography variant="h6">
            {plano.resumo.proximoVencimento ? 
              new Date(plano.resumo.proximoVencimento).toLocaleDateString() :
              'Não há vencimentos próximos'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary">Prestações Pendentes</Typography>
          <Typography variant="h6">
            {plano.resumo.prestacoesPendentes} de {plano.resumo.totalPrestacoes}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary">Valor Total Pendente</Typography>
          <Typography variant="h6" color="primary.main">
            {formatarMoeda(plano.resumo.valorTotalPendente)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary">Situação</Typography>
          <Chip 
            label={plano.resumo.situacaoGeral}
            color={plano.resumo.situacaoGeral === 'REGULAR' ? 'success' : 'error'}
            icon={plano.resumo.situacaoGeral === 'REGULAR' ? <CheckCircleIcon /> : <WarningIcon />}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  if (!user?.tipo === 'estudante') {
    return (
      <Alert severity="warning">
        <AlertTitle>Acesso Restrito</AlertTitle>
        Esta área é apenas para estudantes.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Erro ao carregar planos</AlertTitle>
        {error}
      </Alert>
    );
  }

  if (!planosPagamento || planosPagamento.length === 0) {
    return (
      <Alert severity="info">
        <AlertTitle>Nenhum plano encontrado</AlertTitle>
        Não há planos de pagamento disponíveis no momento.
      </Alert>
    );
  }

  return (
    <Box p={3}>
      {planosPagamento.map((plano) => (
        <Card key={plano.id} sx={{ mb: 4 }}>
          <CardContent>
            {/* Cabeçalho do Plano */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BankIcon sx={{ mr: 2, color: 'primary.main', fontSize: 40 }} />
              <Box>
                <Typography variant="h5">
                  {plano.instituicao} - {plano.anoLetivo}
                </Typography>
                <Typography color="text.secondary">
                  {plano.descricao}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
            
            {/* Resumo Financeiro */}
            {renderResumoFinanceiro(plano)}

            {/* Lista de Prestações */}
            <Grid container spacing={3}>
              {plano.prestacoes?.map((prestacao) => {
                const statusInfo = getStatusInfo(prestacao);
                const detalhesMulta = detalhesMultaPorPrestacao[prestacao.id];

                return (
                  <Grid item xs={12} sm={6} md={4} key={prestacao.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Stack spacing={2}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">
                              {prestacao.mes}
                            </Typography>
                            <Chip
                              icon={<statusInfo.icon fontSize="small" />}
                              label={statusInfo.label}
                              color={statusInfo.color}
                              size="small"
                            />
                          </Box>

                          {/* Valor com tratamento de multa */}
                          {prestacao.vencida && detalhesMulta ? (
                            <Box>
                              <Typography 
                                variant="body1" 
                                color="text.secondary" 
                                sx={{ textDecoration: 'line-through' }}
                              >
                                {formatarMoeda(detalhesMulta.valorPrestacao)}
                              </Typography>
                              <Typography 
                                variant="h6" 
                                color="error.main"
                                sx={{ fontWeight: 'bold' }}
                              >
                                {formatarMoeda(detalhesMulta.valorTotal)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="error.main"
                              >
                                Multa: +{formatarMoeda(detalhesMulta.valorMulta)} 
                                ({((detalhesMulta.valorMulta / detalhesMulta.valorPrestacao) * 100).toFixed(1)}%)
                              </Typography>
                              <Typography 
                                variant="caption" 
                                display="block" 
                                color="error.main"
                              >
                                {detalhesMulta.diasAtraso} dias em atraso
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="h6" color="primary">
                              {formatarMoeda(prestacao.valor)}
                            </Typography>
                          )}

                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Vencimento: {new Date(prestacao.dataVencimento).toLocaleDateString()}
                            </Typography>
                          </Box>

                          {/* Botões de ação */}
                          <Box sx={{ mt: 2 }}>
                            {/* Botão para baixar o comprovativo, só aparece se a prestação estiver paga */}
                            {prestacao.status === 'PAGO' && (
                              <Button
                                startIcon={<ReceiptIcon />}
                                onClick={() => handleDownloadComprovativo(prestacao.id)}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                sx={{ mb: 1 }}
                              >
                                Baixar Recibo
                              </Button>
                            )}

                            {/* Botão de pagamento, aparece apenas se a prestação NÃO estiver paga */}
                            {prestacao.status !== 'PAGO' && (
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={() => handlePagar({
                                  ...prestacao,
                                  valorComMulta: detalhesMulta?.valorTotal
                                })}
                                color={prestacao.vencida ? 'error' : 'primary'}
                                startIcon={<PaymentIcon />}
                              >
                                {prestacao.vencida 
                                  ? `Pagar com Multa (${formatarMoeda(detalhesMulta?.valorTotal)})` 
                                  : 'Pagar'}
                              </Button>
                            )}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
{/* Regras de Multa */}
{plano.regrasMulta?.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Regras de Multa
                </Typography>
                <Grid container spacing={2}>
                  {plano.regrasMulta.map((regra) => (
                    <Grid item xs={12} sm={6} md={4} key={regra.id}>
                      <Alert severity="info" icon={<InfoIcon />}>
                        {regra.descricao}: {regra.percentualMulta}% após {regra.diasAtraso} dias
                      </Alert>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Métodos de Pagamento Disponíveis */}
            {plano.metodosDisponiveis && (
              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Métodos de Pagamento Aceitos
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {Object.entries(plano.metodosDisponiveis)
                    .filter(([, disponivel]) => disponivel)
                    .map(([metodo]) => (
                      <Chip 
                        key={metodo}
                        label={metodo}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Modal de Pagamento */}
      <Dialog
        open={dialogPagamento}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        {prestacaoSelecionada && (
          <RealizarPagamento
            prestacao={prestacaoSelecionada}
            onSuccess={handleSuccess}
            onCancel={handleCloseDialog}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default PlanosPagamentoEstudante;